import { reportErrorToFaro } from '@sendible/common';
import { Button, ChipTextInput, Input, Text, Heading, Box, Dropdown, useToastNotificationContext, TaggableInput } from '@sendible/design-system';
import type { TaggableInputOption } from '@sendible/design-system';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import sharedStateBridge, { useBridgeContext } from '@sendible/shared-state-bridge';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { Option } from '@sendible/design-system/src/components/molecules/TaggableInput';
import { useGetFilters } from '@sendible/frontendv2/src/models/filters';
import {
  ModalComponent,
  ModalHeading,
  InputContainer,
  InputHeader,
  Container,
  Content,
  FullWidth,
  NotificationOptions,
  NotificationCard,
  NotificationRadio,
  ProfileSubText,
  HeadingIcon,
  FooterContainer,
} from './index.styles';
import Skeleton from './skeleton';
import { useRemindersModel, ReminderRequest, ReminderResponse } from '../../models/profiles';

export type XConnectionInitialState = {
  profileName?: string;
  notificationType?: 'Email' | 'Webhook';
  selectedUsers?: TaggableInputOption[];
  selectedClients?: TaggableInputOption[];
};

type XReconnectionProps = {
  closeCallback?: () => void | null;
  accountId?: number | null;
};

const XConnection = ({ closeCallback, accountId }: XReconnectionProps) => {
  const [profileName, setProfileName] = useState('');
  const [notificationType, setNotificationType] = useState<'Email' | 'Webhook'>('Email');
  const [selectedUsers, setSelectedUsers] = useState<TaggableInputOption[]>([]);
  const [selectedClients, setSelectedClients] = useState<TaggableInputOption[]>([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [showWebhookError, setShowWebhookError] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(Boolean(accountId));
  const [isSaving, setIsSaving] = useState(false);
  const [initialUserIds, setInitialUserIds] = useState<number[]>([]);
  const [shouldApplyInitialUsers, setShouldApplyInitialUsers] = useState(false);

  const { t } = useTranslation('x');
  const { showToastNotification } = useToastNotificationContext();
  const [bridgeState] = useBridgeContext();
  const isWhiteLabel = bridgeState?.user?.isWhiteLabel ?? false;
  const { getReminder, createReminder, updateReminder } = useRemindersModel();

  const { data: userData } = useGetFilters(['User']);

  const sharedUser = bridgeState?.user;

  const users = useMemo<TaggableInputOption[]>(() => {
    if (!userData?.users) return [];

    const currentUser = sharedUser?.userId;

    return userData.users.map((user) => ({
      label: user.label as string,
      action: user.id,
      isLocked: parseInt(user.id, 10) === currentUser,
    }));
  }, [userData, sharedUser]);

  const showErrorToast = (messageKey: string) => {
    showToastNotification({
      hideTimeout: 10,
      notificationText: t(messageKey, { name: profileName.trim() }) as string,
      type: NotificationTypeEnum.Error,
      showCloseButton: true,
    });
  };

  const applyReminderState = useCallback((reminder: ReminderResponse) => {
    const settings = reminder.notificationSettings ?? {
      type: 'Email',
      userIds: [],
      emails: [],
      webhookUrl: null,
    };
    const userIds = settings.userIds ?? [];

    setIsEdit(true);
    setProfileName(reminder.name ?? '');
    setNotificationType(settings.type === 'Webhook' ? 'Webhook' : 'Email');
    setInitialUserIds(userIds);
    setShouldApplyInitialUsers(userIds.length > 0);
    setSelectedClients((settings.emails ?? []).map((email) => ({ label: email, action: email })));
    setWebhookUrl(settings.webhookUrl ?? '');
    setShowWebhookError(false);

    if (userIds.length === 0) {
      setSelectedUsers([]);
    }
  }, []);

  const isWebhookValid = useMemo(() => {
    const url = webhookUrl.trim();

    if (!url) return false;

    try {
      const parsed = new URL(url);

      return ['http:', 'https:'].includes(parsed.protocol);
    } catch {
      return false;
    }
  }, [webhookUrl, notificationType]);

  const closeModal = () => {
    if (closeCallback) closeCallback();

    sharedStateBridge.updateSpecificKey('app', 'xConnectionModal', {
      isConnectOpen: false,
      accountId: null,
    });
  };

  const moreOptionSelect = (option: string): void => {
    switch (option) {
      case 'tags':
        window.Backbone.trigger(`MENU_TAGS_${accountId}`);
        break;
      case 'transfer':
        window.Backbone.trigger(`MENU_TRANSFER_${accountId}`);
        break;
      case 'share':
        window.Backbone.trigger(`MENU_SHARING_${accountId}`);
        break;
      case 'delete':
        window.Backbone.trigger(`DELETE_SERVICE_${accountId}`);
        break;
      default:
        break;
    }
  };

  const selectedClientEmails = useMemo(
    () =>
      selectedClients
        .map((client) => {
          if (client.action) return String(client.action);

          return client.label;
        })
        .filter((email): email is string => Boolean(email)),
    [selectedClients]
  );

  const effectiveSelectedUsers = useMemo(() => {
    if (!shouldApplyInitialUsers || !initialUserIds.length || !users.length) {
      return selectedUsers;
    }

    return users.filter((user) => initialUserIds.includes(Number(user.action)));
  }, [shouldApplyInitialUsers, initialUserIds, users, selectedUsers]);

  const isCreateDisabled = useMemo(() => {
    return isSaving || !profileName.trim() || (notificationType === 'Webhook' && (webhookUrl.trim().length === 0 || !isWebhookValid));
  }, [isSaving, profileName, notificationType, webhookUrl, isWebhookValid]);

  const buildReminderPayload = (): ReminderRequest | null => {
    const parsedUserIds = effectiveSelectedUsers.map((user) => Number(user.action)).filter((id) => !Number.isNaN(id));

    return {
      name: profileName.trim(),
      notificationSettings: {
        type: notificationType,
        userIds: parsedUserIds,
        emails: selectedClientEmails,
        webhookUrl: notificationType === 'Webhook' ? webhookUrl.trim() : null,
      },
    };
  };

  const handleSave = async () => {
    const payload = buildReminderPayload();

    if (!payload) {
      return;
    }

    setIsSaving(true);

    try {
      if (isEdit) {
        await updateReminder(accountId as number, payload);

        showToastNotification({
          hideTimeout: 10,
          notificationText: t('reminder_updated_successfully', { name: profileName.trim() }) as string,
          type: NotificationTypeEnum.Success,
          showCloseButton: true,
        });
      } else {
        await createReminder(payload);

        showToastNotification({
          hideTimeout: 10,
          notificationText: t('reminder_created_successfully', { name: profileName.trim() }) as string,
          type: NotificationTypeEnum.Success,
          showCloseButton: true,
        });
      }

      window.Backbone.trigger('x-reminder-connected');

      closeModal();
    } catch (error) {
      reportErrorToFaro(error, 'XConnection saveReminder');

      if (isEdit) {
        showErrorToast('could_not_update_reminder');
      } else {
        showErrorToast('could_not_add_reminder');
      }

      setIsSaving(false);
    }
  };

  useEffect(() => {
    document.documentElement.classList.add('prevent-scroll');

    return () => {
      document.documentElement.classList.remove('prevent-scroll');
    };
  }, []);

  useEffect(() => {
    if (accountId == null) return;

    const fetchReminder = async () => {
      setIsLoading(true);

      try {
        const reminder = await getReminder(accountId);

        setIsLoading(false);

        if (reminder) {
          applyReminderState(reminder);
        } else {
          throw new Error('X reminder not found');
        }
      } catch (error) {
        reportErrorToFaro(error, 'XConnection loadReminder');
        showErrorToast('could_not_load_reminder');
        closeModal();
      }
    };

    fetchReminder();
  }, []);

  return (
    <ModalComponent
      zIndex={1040}
      variation="medium"
      headingTitle={
        <ModalHeading>
          <HeadingIcon
            name="x"
            color="brandDark"
          />
          {t('setup_reminder')}
        </ModalHeading>
      }
      close={closeModal}
      footerContent={
        <FooterContainer
          fullWidth
          contentJustify="flex-end"
        >
          {(isEdit || accountId) && (
            <Dropdown
              options={[
                { label: t('tags') as string, action: 'tags' },
                { label: t('transfer') as string, action: 'transfer' },
                { label: t('share') as string, action: 'share' },
                { type: 'divider' },
                { label: t('delete') as string, action: 'delete' },
              ]}
              select={moreOptionSelect}
              placement="top-end"
              width="9rem"
            >
              <Button
                label={t('more') as string}
                size={14}
                disabled={isCreateDisabled}
                variation="outline"
              />
            </Dropdown>
          )}
          <Button
            appearance="primary"
            disabled={isCreateDisabled}
            isLoading={isSaving}
            label={(isEdit ? t('edit_button') : t('create_button')) as string}
            onClick={handleSave}
            size={14}
            variation="fill"
          />
        </FooterContainer>
      }
    >
      {isLoading ? (
        <Skeleton />
      ) : (
        <Container>
          <Content>
            <InputContainer>
              <InputHeader>{t('profile_name')}</InputHeader>
              <FullWidth>
                <Input
                  ariaLabel={t('profile_name') as string}
                  change={(value: string) => {
                    setProfileName(value);
                  }}
                  value={profileName}
                  label={t('profile_name_placeholder') as string}
                  maxLength={50}
                />
              </FullWidth>
              <ProfileSubText>{t('use_profile_reference')}</ProfileSubText>
            </InputContainer>
            <InputContainer>
              <NotificationOptions
                role="radiogroup"
                aria-label={t('notification_type') as string}
              >
                <NotificationCard
                  type="button"
                  onClick={() => setNotificationType('Email')}
                  $selected={notificationType === 'Email'}
                  role="radio"
                  tabIndex={notificationType === 'Email' ? 0 : -1}
                >
                  <NotificationRadio $selected={notificationType === 'Email'}>
                    <input
                      type="radio"
                      value="Email"
                      onClick={() => setNotificationType('Email')}
                      aria-label={t('notification_email_description') as string}
                      readOnly
                    />
                    <div>
                      <Heading
                        semantic="h3"
                        variation="component-title_14"
                      >
                        {t('email')}
                      </Heading>
                      <Text variation="body_12">{t('notification_email_description')}</Text>
                    </div>
                  </NotificationRadio>
                </NotificationCard>
                <NotificationCard
                  type="button"
                  onClick={() => setNotificationType('Webhook')}
                  $selected={notificationType === 'Webhook'}
                  role="radio"
                  tabIndex={notificationType === 'Webhook' ? 0 : -1}
                >
                  <NotificationRadio $selected={notificationType === 'Webhook'}>
                    <input
                      type="radio"
                      value="Webhook"
                      onClick={() => setNotificationType('Webhook')}
                      aria-label={t('notification_webhook_description') as string}
                      readOnly
                    />
                    <div>
                      <Heading
                        semantic="h3"
                        variation="component-title_14"
                      >
                        {t('webhook')}
                      </Heading>
                      <Text variation="body_12">{t('notification_webhook_description')}</Text>
                      {!isWhiteLabel && (
                        <a
                          href="https://support.sendible.com/hc/en-us/articles/31797068522141"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link"
                        >
                          <span>{t('notification_webhook_learn_more')}</span> <span className="icon-span">↗</span>
                        </a>
                      )}
                    </div>
                  </NotificationRadio>
                </NotificationCard>
              </NotificationOptions>
              {notificationType === 'Webhook' && (
                <FullWidth style={{ marginTop: '12px' }}>
                  <InputHeader>{t('webhook_url')}</InputHeader>
                  <Text
                    variation="body_12"
                    color="brandDarkTint30"
                  >
                    {t('webhook_url_description')}
                  </Text>
                  <Box
                    fullWidth
                    margin={['s12', 's0', 's0', 's0']}
                  >
                    <Input
                      ariaLabel={t('webhook_url_placeholder') as string}
                      label={t('webhook_url_placeholder') as string}
                      value={webhookUrl}
                      change={(value: string) => {
                        setWebhookUrl(value);
                        setShowWebhookError(false);
                      }}
                      feedback={showWebhookError ? { type: 'danger', message: t('webhook_url_invalid') as string } : undefined}
                      blur={() => {
                        setShowWebhookError(!isWebhookValid);
                      }}
                    />
                  </Box>
                </FullWidth>
              )}
            </InputContainer>
            {notificationType === 'Email' && (
              <>
                <InputContainer>
                  <InputHeader>{t('notify_team_members')}</InputHeader>
                  <Text
                    variation="body_12"
                    color="brandDarkTint30"
                  >
                    {t('notify_team_members_subtext')}
                  </Text>
                  <Box
                    fullWidth
                    margin={['s12', 's0', 's0', 's0']}
                  >
                    <TaggableInput
                      id="notify-users"
                      emptyStateLabel={t('no_users_found') as string}
                      placeholder={selectedUsers.length >= 10 ? '' : t('add_users')}
                      onSelect={(value: Option[]) => {
                        if (shouldApplyInitialUsers) {
                          setShouldApplyInitialUsers(false);
                        }
                        setSelectedUsers(value);
                      }}
                      options={users}
                      selectedOptions={effectiveSelectedUsers}
                      limit={10}
                      placement="bottom-start"
                    />
                  </Box>
                </InputContainer>
                <InputContainer>
                  <InputHeader>{t('notify_clients')}</InputHeader>
                  <Text
                    variation="body_12"
                    color="brandDarkTint30"
                  >
                    {t('add_external_text')}
                  </Text>
                  <Box
                    fullWidth
                    margin={['s12', 's0', 's0', 's0']}
                  >
                    <ChipTextInput
                      id="external-clients-input"
                      placeholder={t('add_clients') as string}
                      onChange={(chips) => setSelectedClients(chips.map((c) => ({ label: c, action: c })))}
                      chips={selectedClientEmails}
                      errorText={t('invalid_email') as string}
                      max={5}
                      errorMaxText={t('max_clients_reached') as string}
                    />
                  </Box>
                </InputContainer>
              </>
            )}
          </Content>
        </Container>
      )}
    </ModalComponent>
  );
};

export default XConnection;
